import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, Trash, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";

const MODES = [["both", "Pago y cotización"], ["payment", "Solo pago"], ["quote", "Solo cotización"]];

export default function AdminAddons() {
  const [services, setServices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get("/admin/addon-services").then((r) => setServices(r.data || [])).catch(() => {});
    api.get("/admin/addon-requests").then((r) => setRequests(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setField = (id, k, v) => setServices((s) => s.map((x) => x.id === id ? { ...x, [k]: v } : x));
  const addNew = async () => {
    try { const { data } = await api.post("/admin/addon-services", { name: "Nuevo servicio", price: 0, mode: "both", description: "" }); setServices((s) => [...s, data]); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveOne = async (s) => {
    setSaving(true);
    try { await api.put(`/admin/addon-services/${s.id}`, s); toast.success("Servicio guardado"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const del = async (s) => {
    if (!window.confirm(`¿Eliminar "${s.name}"?`)) return;
    try { await api.delete(`/admin/addon-services/${s.id}`); setServices((x) => x.filter((y) => y.id !== s.id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setReqStatus = async (rid, status) => {
    try { await api.put(`/admin/addon-requests/${rid}`, { status }); setRequests((r) => r.map((x) => x.id === rid ? { ...x, status } : x)); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-addons">
      <div className="border border-white/10 rounded-xl p-6 bg-[#121212]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Servicios adicionales</h3>
          <Button onClick={addNew} size="sm" variant="outline" className="rounded-full border-gold/30 text-gold hover:bg-gold/10" data-testid="add-addon-btn"><Plus size={14} className="mr-1" /> Añadir servicio</Button>
        </div>
        <div className="space-y-4">
          {services.map((s) => (
            <div key={s.id} className="bg-white/5 rounded-lg p-4" data-testid={`admin-addon-${s.id}`}>
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2"><Label className="text-xs">Nombre</Label><Input value={s.name} onChange={(e) => setField(s.id, "name", e.target.value)} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`addon-name-${s.id}`} /></div>
                <div><Label className="text-xs">Precio $</Label><Input type="number" value={s.price} onChange={(e) => setField(s.id, "price", parseFloat(e.target.value) || 0)} className="mt-1 bg-white/5 border-white/15 h-9" data-testid={`addon-price-${s.id}`} /></div>
              </div>
              <div className="mt-3"><Label className="text-xs">Descripción</Label><textarea value={s.description || ""} onChange={(e) => setField(s.id, "description", e.target.value)} rows={2} className="mt-1 w-full bg-white/5 border border-white/15 rounded-lg p-2 text-sm text-neutral-200" /></div>
              <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                <div className="flex gap-2">
                  {MODES.map(([m, lbl]) => (
                    <button key={m} onClick={() => setField(s.id, "mode", m)} data-testid={`addon-mode-${s.id}-${m}`}
                      className={`text-xs px-3 py-1 rounded-full border ${s.mode === m ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400"}`}>{lbl}</button>
                  ))}
                  <label className="text-xs text-neutral-400 flex items-center gap-1 ml-2"><input type="checkbox" checked={s.active !== false} onChange={(e) => setField(s.id, "active", e.target.checked)} /> Activo</label>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => saveOne(s)} disabled={saving} className="rounded-full bg-gold text-obsidian font-semibold" data-testid={`addon-save-${s.id}`}>{saving ? <CircleNotch className="animate-spin" /> : "Guardar"}</Button>
                  <button onClick={() => del(s)} className="text-red-400 hover:text-red-300 px-2" data-testid={`addon-delete-${s.id}`}><Trash size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212]">
        <h3 className="font-semibold mb-4">Solicitudes de servicios ({requests.length})</h3>
        {requests.length === 0 ? <p className="text-sm text-neutral-500">Sin solicitudes aún.</p> : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-3 bg-white/5 rounded-lg p-3 text-sm" data-testid={`addon-req-${r.id}`}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{r.service_name} · <span className="text-neutral-400">{r.owner_email}</span></div>
                  {r.note && <div className="text-xs text-neutral-500 truncate">{r.note}</div>}
                </div>
                <select value={r.status} onChange={(e) => setReqStatus(r.id, e.target.value)} className="bg-white/5 border border-white/15 rounded px-2 py-1 text-xs" data-testid={`addon-req-status-${r.id}`}>
                  <option value="requested">Solicitado</option>
                  <option value="in_progress">En progreso</option>
                  <option value="done">Completado</option>
                  <option value="cancelled">Cancelado</option>
                </select>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
